import { createInertiaApp } from '@inertiajs/react'
import { createElement, ReactNode } from 'react'
import { createRoot } from 'react-dom/client'
import { ThemeProvider } from 'next-themes'
import * as Sentry from '@sentry/react'
import { configureI18n } from '@/lib/i18n'

// Temporary type definition, until @inertiajs/react provides one
type ResolvedComponent = {
  default: ReactNode
  layout?: (page: ReactNode) => ReactNode
}

type SharedI18n = {
  locale?: string
  defaultLocale?: string
}

// DSN + environment arrive at runtime via Inertia shared props (see
// SentrySharing concern) because prod and staging share one Vite bundle.
type SharedSentry = {
  dsn?: string
  environment?: string
}

function initSentry(config: SharedSentry | undefined) {
  if (!config?.dsn) return

  Sentry.init({
    dsn: config.dsn,
    environment: config.environment,
    // Release is baked at build time so it matches the uploaded source maps.
    release: import.meta.env.VITE_SENTRY_RELEASE,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.1,
  })
}

// Minimal, dependency-free fallback shown when a render crashes. It must not
// reuse app UI or i18n, since either could be what failed.
function errorFallback() {
  return createElement(
    'div',
    { style: { padding: '2rem', textAlign: 'center', fontFamily: 'system-ui, sans-serif' } },
    createElement('h1', { style: { fontSize: '1.25rem', marginBottom: '0.5rem' } }, 'Algo salió mal'),
    createElement('p', { style: { color: '#666', marginBottom: '1rem' } }, 'Ya lo estamos revisando. Intenta recargar la página.'),
    createElement('button', {
      onClick: () => window.location.reload(),
      style: { padding: '0.5rem 1rem', cursor: 'pointer' },
    }, 'Recargar'),
  )
}

createInertiaApp({
  // Set default page title
  // see https://inertia-rails.dev/guide/title-and-meta
  //
  // title: title => title ? `${title} - App` : 'App',

  // Disable progress bar
  //
  // see https://inertia-rails.dev/guide/progress-indicators
  // progress: false,

  resolve: (name) => {
    const pages = import.meta.glob<ResolvedComponent>('../pages/**/*.tsx', {
      eager: true,
    })
    const page = pages[`../pages/${name}.tsx`]
    if (!page) {
      console.error(`Missing Inertia page component: '${name}.tsx'`)
    }
    // To use a default layout, import the Layout component
    // and use the following line.
    // see https://inertia-rails.dev/guide/pages#default-layouts
    //
    // page.default.layout ||= (page) => createElement(Layout, null, page)

    return page
  },

  setup({ el, App, props }) {
    const sharedI18n = props.initialPage.props.i18n as SharedI18n | undefined
    configureI18n(sharedI18n)

    initSentry(props.initialPage.props.sentry as SharedSentry | undefined)

    if (el) {
      createRoot(el).render(
        createElement(Sentry.ErrorBoundary, { fallback: errorFallback },
          createElement(ThemeProvider, { attribute: "class", defaultTheme: "system", enableSystem: true },
            createElement(App, props)
          )
        )
      )
    } else {
      console.error(
        'Missing root element.\n\n' +
          'If you see this error, it probably means you load Inertia.js on non-Inertia pages.\n' +
          'Consider moving <%= vite_typescript_tag "inertia" %> to the Inertia-specific layout instead.',
      )
    }
  },
})
